import {LocalStorageController, StorageUpdateInfo, StorageUpgradeResponse} from './LocalStorageController';
import {deleteDB, openDB} from 'idb';

const targetVersion = 8;

const migrationFunctions: Map<number,
    {
        migrationFunction: ((event: IDBVersionChangeEvent) => Promise<{ success: boolean }>),
        rollbackFunction?: ((event: IDBVersionChangeEvent) => Promise<{ success: boolean, error?: string }>)
    }> = new Map([
    [7, {
        migrationFunction: async (e) => {
            const db = await (e.target as any)?.result;
            const stores = db.objectStoreNames;
            const tables = [
                'ServiceAppDB', // DB of ServiceApp containing lots of crossover data with TimeApp
                'SwissWebConfigDB', // Kinda random data that might as well be in Main
                'SwissWebMainDB', // Objects and Addresses and so on
                'SwissTimeEntry', // Db used for TimeApp containing lots of crossover data with ServiceApp
                'ServiceAppDB_Doku', // Dokument used in ServiceApp
                'SwissInternDB', // used for exactly one entry rapport header for whatever reason
                'SwissServices', // Table used for tracking of ServiceApp and TimeApp (supplies and times)
                'DocumentSynchronisationQueue', // Documents that need to be synced
            ];
            // we need to check if stores exists since they are added in whatever version
            for (const tableName of tables) {
                if (!stores.contains(tableName)) {
                    await db.createObjectStore(tableName);
                }
            }
            console.log('Added Stores for V7');
            return {success: true};
        },
        rollbackFunction: async (e) => {
            alert('Die Lokale DB konnte nicht erstellt werden. Bitte wenden Sie sich an den Support.');
            return {success: false, error: 'could not init indexDB'};
        },
    }],
    [8, {
        migrationFunction: async (e) => {
            console.log('no migration required');
            return {success: true};
        },
        rollbackFunction: async (e) => {
            return {success: false, error: 'no migration for v8 needed'};
        },
    }]
]);

export class IndexDBController implements LocalStorageController {
    version = targetVersion;

    async upgradeDB(): Promise<StorageUpgradeResponse> {
        return new Promise(async (resolve, reject) => {
            if (!window.indexedDB) {
                alert('Ihr Browser unterstützt keine Offline-Speicherung von Daten.');
                resolve({status: 'no-support'});
            }

            try {
                await openDB('SwissSoftDB', this.version, {
                    upgrade: async (db, oldVersion, newVersion, transaction, event) => {
                        return resolve({
                            status: 'updated',
                            updateInfos: await this.upgradeIndexDB(event)
                        });
                    }
                });
                return resolve({status: 'success'});
            } catch (error) {
                if (error instanceof DOMException && (error as DOMException).name === 'VersionError') {
                    const versionInfo = error.message.match(/\(\d+\)/g);
                    if (versionInfo?.length !== 2) {
                        alert('Ein unerwarteter Fehler ist aufgetreten. Bitte wenden Sie sich an den Support.');
                        return reject({status: 'error', error});
                    }
                    const newerVersion = parseInt(versionInfo[1].substring(1, versionInfo[1].length - 1), 10);
                    console.warn(`using newer DB version ${newerVersion} instead of ${this.version}`);
                    this.version = newerVersion;
                    return resolve(await this.upgradeDB());
                }
                alert('Ein unerwarteter Fehler ist aufgetreten. Bitte wenden Sie sich an den Support.');
                return reject({status: 'error', error});
            }
        });
    }

    public async add(storageName: string, value: any, keyName: string): Promise<string> {
        return this.internalAdd(storageName, value, keyName);
    }

    public async get<T>(storageName: string, keyName: string, defaultValue: T): Promise<T> {
        return this.internalGet<T>(storageName, keyName, defaultValue);
    }

    public async delete(storageName: string, keyName: string): Promise<string> {
        return this.internalDelete(storageName, keyName);
    }

    public async getKeys(storageName: string): Promise<string[]> {
        const db = await openDB('SwissSoftDB', this.version, {
            upgrade: async (database, oldVersion, newVersion, transaction, event) => {
                if (!(await this.upgradeIndexDB(event)).success) {
                    throw new Error('IndexDB could not be Upgraded');
                }
            }
        });
        const store: any[] = await db
            .transaction([storageName], 'readonly')
            .objectStore(storageName)
            .getAllKeys();
        return store || [];
    }

    public async getAllData(): Promise<any> {
        const db = await openDB('SwissSoftDB', this.version, {
            upgrade: async (database, oldVersion, newVersion, transaction, event) => {
                if (!(await this.upgradeIndexDB(event)).success) {
                    throw new Error('IndexDB could not be Upgraded');
                }
            }
        });
        const obj: any = {};
        for (const name of db.objectStoreNames) {
            obj[name] = {};
            const keys = await db.transaction([name]).objectStore(name).getAllKeys();
            for (let j = 0; j < keys.length; j++) {
                obj[name][(keys as any[])[j]] = {};
                obj[name][(keys as any[])[j]] = await this.get(name, (keys as any[])[j], true);
            }
        }
        return obj;
    }

    public async deleteDatabase(): Promise<any> {
        await deleteDB('SwissSoftDB', {
            blocked: async (currentVersion, event) => {
                throw new Error('db locked and cant be deleted');
            }
        });
    }

    private async internalDelete(storageName: string, keyName: string, tries = 0): Promise<string> {
        try {
            const db = await openDB('SwissSoftDB', this.version, {
                upgrade: async (database, oldVersion, newVersion, transaction, event) => {
                    if (!(await this.upgradeIndexDB(event)).success) {
                        throw new Error('IndexDB could not be Upgraded');
                    }
                }
            });
            await db.transaction([storageName], 'readwrite')
                .objectStore(storageName)
                .delete(keyName);
        } catch (error) {
            if (error instanceof DOMException && (error as DOMException).name === 'NotFoundError') {
                if (tries < 1) {
                    const res = await this.upgradeDB();
                    if (res.status === 'updated' || res.status === 'success') {
                        return this.internalDelete(storageName, keyName, tries + 1);
                    }
                }
                alert(`Es konnte nicht auf die interne DB zugegriffen. \n Bitte wenden Sie sich an den Support. \n ${error.message}`);
                return 'fail';
            }
        }
        return 'success';
    }

    private async internalAdd(storageName: string, value: any, keyName: string, tries = 0): Promise<string> {
        try {
            const db = await openDB('SwissSoftDB', this.version, {
                upgrade: async (database, oldVersion, newVersion, transaction, event) => {
                    if (!(await this.upgradeIndexDB(event)).success) {
                        throw new Error('IndexDB could not be Upgraded');
                    }
                }
            });
            await db
                .transaction([storageName], 'readwrite')
                .objectStore(storageName)
                .put(value, keyName);
            return 'success';
        } catch (error) {
            if (error instanceof DOMException && (error as DOMException).name === 'NotFoundError') {
                // store could not be found its fair to assume a version mismatch
                if (tries < 1) {
                    const res = await this.upgradeDB();
                    if (res.status === 'updated' || res.status === 'success') {
                        return this.internalAdd(storageName, value, keyName, tries + 1);
                    }
                }
                alert(`Es konnte nicht auf die interne DB zugegriffen. \n Bitte wenden Sie sich an den Support. \n ${error.message}`);
            }
            if (error instanceof DOMException && (error as DOMException).name === 'VersionError') {
                alert(`Es konnte nicht auf die interne DB zugegriffen. \n Bitte wenden Sie sich an den Support. \n ${error.message}`);
            }
            throw error;
        }
    }

    private async internalGet<T>(storageName: string, keyName: string, defaultValue: T, tries = 0): Promise<T> {
        return new Promise<any>(async (resolve, reject) => {
            try {
                const db = await openDB<any>('SwissSoftDB', this.version, {
                    upgrade: async (database, oldVersion, newVersion, transaction, event) => {
                        if (!(await this.upgradeIndexDB(event)).success) {
                            throw new Error('IndexDB could not be Upgraded');
                        }
                    }
                });
                const store = await db
                    .transaction([storageName], 'readonly')
                    .objectStore(storageName)
                    .get(keyName);
                if (store === undefined) {
                    if (defaultValue !== undefined) {
                        return resolve(defaultValue);
                    } else {
                        return resolve(false);
                    }
                }
                return resolve(store);
            } catch (error) {
                if (error instanceof DOMException && (error as DOMException).name === 'NotFoundError') {
                    // store could not be found its fair to assume a version mismatch
                    if (tries < 1) {
                        const res = await this.upgradeDB();
                        if (res.status === 'updated' || res.status === 'success') {
                            return this.internalGet<T>(storageName, keyName, defaultValue, tries + 1);
                        }
                    }
                    alert(`Es konnte nicht auf die interne DB zugegriffen.\n Store ${storageName} konnte nicht gefunden werden in DB v${this.version}. \n Bitte wenden Sie sich an den Support.`);
                }
                return reject(error);
            }
        });
    }

    private async upgradeIndexDB(event: IDBVersionChangeEvent): Promise<StorageUpdateInfo> {
        if (event.newVersion === null) { // IndexDB was deleted
            return {
                from: 0,
                to: 0,
                success: true,
                deletion: true
            };
        }

        const fromVersion = event.oldVersion < 7 ? 6 : event.oldVersion; // min version is 7
        for (let i = fromVersion + 1; i <= event.newVersion; i++) {
            const f = migrationFunctions.get(i);
            if (f?.migrationFunction) {
                const res = await f.migrationFunction(event);
                if (!res.success) {
                    if (f.rollbackFunction) {
                        const rollbackRes = await f.rollbackFunction(event);
                        if (!rollbackRes.success) {
                            alert('Fehler beim Zurückrollen des Updates . Bitte wenden Sie sich an den Support.');
                            return {
                                from: event.oldVersion,
                                to: i,
                                success: false,
                                error: rollbackRes.error
                            };
                        }
                        alert(`Fehler beim Update der lokalen DB auf Version ${i}.\n Version ${i - 1} wurde wiederhergestellt. \n Bitte wenden Sie sich an den Support.`);
                        return {
                            from: event.oldVersion,
                            to: i,
                            success: false,
                            error: `rolled back to previous version ${i - 1}`
                        };
                    }
                    return {
                        from: event.oldVersion,
                        to: i,
                        success: false,
                        error: `no rollback function available for ${i}`
                    };
                }
            }
        }
        return {
            from: event.oldVersion,
            to: event.newVersion,
            success: true
        };
    }
}

